import React from 'react';

import {
  MetadataCollectorActions,
  useMetadataCollector,
} from '@mint-lib/agentic-ai-circuit';
import { useAiSidebarContext } from '@mint-lib/ai-sidebar-circuit';
import { useTranslation } from '@mint-lib/i18n';
import { Tabs, TabsList, TabsTrigger } from '@mint-lib/tw-components';
import { useSetAtom } from 'jotai';

import { useSuggestionFilter } from '../../../hooks/useSuggestionFilter.js';
import { selectedSuggestionAtom } from '../../../store/index.js';
import { SuggestionFilterActions } from '../../../store/suggestion/suggestionFilter.js';
import { SuggestionTabs } from '../../../types/suggestion.js';
import { getSuggestionTabs } from '../../../utils/getTabs.js';
import NotReadBadge from '../NotReadBadge/NotReadBadge.jsx';

type SuggestionTabsFilterProps = {
  activeTab: SuggestionTabs;
  setActiveTab: (tab: SuggestionTabs) => void;
  hasUnreadSuggestions?: boolean;
};

const SuggestionTabsFilter = ({
  activeTab,
  setActiveTab,
  hasUnreadSuggestions,
}: SuggestionTabsFilterProps) => {
  const { setSuggestionFilter } = useSuggestionFilter();
  const { setAgenticMetadata } = useMetadataCollector();

  const { t } = useTranslation('@mint-lib/recommendations-circuit');

  const setSuggestion = useSetAtom(selectedSuggestionAtom);

  const { setSelectedSuggestion, setIsAiSidebarOpen } = useAiSidebarContext();

  const allSuggestionTabs = getSuggestionTabs(t);

  const newTabValue = allSuggestionTabs[0].value;

  const setTabAndFilter = (tab: SuggestionTabs) => {
    setActiveTab(tab);

    setSuggestionFilter({
      type: SuggestionFilterActions.RESET,
      data: tab.toUpperCase(),
    });

    setAgenticMetadata({
      type: MetadataCollectorActions.RESET_RECOMMENDATION,
    });
  };

  const handleChangeTab = (_: React.SyntheticEvent, tab: SuggestionTabs) => {
    setTabAndFilter(tab);

    setSuggestion(null);
    setSelectedSuggestion(null);
    setIsAiSidebarOpen(false);
  };

  return (
    <Tabs value={activeTab} className="w-full max-w-100">
      <TabsList className="flex w-full">
        {allSuggestionTabs.map((tab) => (
          <TabsTrigger
            className="w-full items-center justify-center"
            key={tab.value}
            aria-label={`${tab.value}-tab`}
            value={tab.value}
            onClick={(e) => handleChangeTab(e, tab.value)}
          >
            {tab.value === newTabValue ? (
              <div className="flex min-w-0 flex-row gap-0.5">
                <NotReadBadge invisible={!hasUnreadSuggestions} />
                {tab.label}
              </div>
            ) : (
              tab.label
            )}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
};
export default SuggestionTabsFilter;
